import './style.css'
import * as THREE from 'three'
import * as dat from 'dat.gui'
import gsap from 'gsap'

import { images } from '../static/images.js'
import { map } from './scripts/utils.js'

import vertexShader from './glsl/vertexShader.glsl'
import fragmentShader from './glsl/fragmentShader.glsl'


/**
 * Base
 */
// Debug
const gui = new dat.GUI()
gui.hide()

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color('#B9AEA6');

/**
 * Loaders
 */
const loaderElement = document.querySelector('.progress')
const tree = document.querySelector('.tree')

const loadingManager = new THREE.LoadingManager(
    () => {
        setTimeout(() => {
            gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 2, value: 0 })
            loaderElement.style.opacity = 0
            tree.style.opacity = 0
        }, 500)
    },

    // Progress
    (itemUrl, itemsLoaded, itemsTotal) => {
        loaderElement.style.strokeDashoffset = 100 - (itemsLoaded / itemsTotal) * 100;
    }
)
const textureLoader = new THREE.TextureLoader(loadingManager);
const fontLoader = new THREE.FontLoader(loadingManager)



/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneGeometry(3, 3, 1, 1)
const overlayMaterial = new THREE.ShaderMaterial({
    transparent: true,
    vertexShader: `
        void main()
        {
            gl_Position = vec4(position, 1.0);
        }
    `,
    fragmentShader: `
    uniform float uAlpha;
    uniform vec3 uColor;
        void main()
        {
            gl_FragColor = vec4(uColor, uAlpha);
        }
    `,
    uniforms:
    {
        uAlpha: { value: 1 },
        uColor: { value: new THREE.Color('#B9AEA6')}
    },
})
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
scene.add(overlay)


/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.z = 2;
camera.position.y = 4
scene.add(camera)

/**
 * Planes
 */

const planes = [];
const iPositions = [];
const widthTotal = (1.5 + 1) * 12;

const radius = 4

const planeGeometry = new THREE.PlaneGeometry(1.5, 2, 50, 50)

for (const [i, image] of images.entries()) {
    const imageTexture = textureLoader.load(image.url)

    const material = new THREE.ShaderMaterial({
        uniforms: {
            uMap: { value: imageTexture },
            uTime: { value: 0 },
            uFrequency: { value: 2 },
            uActivation: { value: 0 }
        },
        vertexShader,
        fragmentShader
    })

    const mesh = new THREE.Mesh(planeGeometry, material)

    const x = Math.cos(i / 12 * Math.PI * 2) * radius        // Get the x position using cosinus
    const y = Math.sin(i / 12 * Math.PI * 2) * radius        // Get the z position using sinus4
    mesh.position.x = x;
    mesh.position.y = y;

    mesh.rotation.z = map(mesh.position.x, -widthTotal, widthTotal, Math.PI, -Math.PI)

    scene.add(mesh)
    planes.push(mesh)
    iPositions.push(i)
}

/**
 * Text
 */
const titles = [];
const indexsText = [];

const createTitle = (scene, fontLoader, text, indexImage, index) => {
    indexImage = indexImage.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

    const x = Math.cos(index / 12 * Math.PI * 2) * radius
    const y = Math.sin(index / 12 * Math.PI * 2) * radius
    const material = new THREE.MeshBasicMaterial({ color: 0x000000 })

    fontLoader.load('/fonts/Cormorant_Upright_Regular.json',
        (font) => {
            const textGeometry = new THREE.TextGeometry(
                text,
                {
                    font: font,
                    size: 0.1,
                    height: 0.001,
                    material: 0,
                    bevelThickness: 1,
                    extrudeMaterial: 1,
                }
            )

            textGeometry.center()

            const titleMesh = new THREE.Mesh(textGeometry, material);
            titleMesh.position.x = x
            titleMesh.position.y = y - 1.25
            scene.add(titleMesh)
            titles.push(titleMesh)
        })

    fontLoader.load('/fonts/Cormorant_Upright_Regular.json',
        (font) => {
            const textGeometry = new THREE.TextGeometry(
                indexImage,
                {
                    font: font,
                    size: 0.05,
                    height: 0.001,
                    material: 0,
                    bevelThickness: 1,
                    extrudeMaterial: 1
                }
            )

            textGeometry.center()

            const titleMesh = new THREE.Mesh(textGeometry, material);
            titleMesh.position.x = x
            titleMesh.position.y = y - 1.1
            scene.add(titleMesh)
            indexsText.push(titleMesh)
        })
}

for (const [i, image] of images.entries()) {
    createTitle(scene, fontLoader, image.name, image.index, i);
}

/**
 * Mouse
 */
const mouse = new THREE.Vector2()

window.addEventListener('mousemove', (event) => {
    mouse.x = event.clientX / sizes.width * 2 - 1
    mouse.y = - (event.clientY / sizes.height) * 2 + 1
})

const raycaster = new THREE.Raycaster()

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.toneMapping = THREE.ACESFilmicToneMapping

/**
 * EventListener
 */
function onWindowResize() {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
}

function onWheel(e) {
    for (let [i, plane] of planes.entries()) {

        if (e.deltaY < 0) {
            if (iPositions[i] > -1) {
                iPositions[i] -= 0.1;
            } else {
                iPositions[i] = 11;
            }
        }
        else if (e.deltaY > 0) {
            if (iPositions[i] < 11) {
                iPositions[i] += 0.1;
            } else {
                iPositions[i] = -0.9;
            }
        }
        const x = Math.cos(iPositions[i] / 12 * Math.PI * 2) * radius
        const y = Math.sin(iPositions[i] / 12 * Math.PI * 2) * radius

        // const xTitle = Math.cos(iPositions[i] / 12 * Math.PI * 2) * radiusTitle
        // const yTitle = Math.sin(iPositions[i] / 12 * Math.PI * 2) * radiusTitle

        gsap.to(plane.position, { x: x, y: y, duration: 0.5 })

        if (titles[i]) {
            gsap.to(titles[i].position, { x: x, y: y - 1.25, duration: 0.5 })
        }
        if (indexsText[i]) {
            gsap.to(indexsText[i].position, { x: x, y: y - 1.1, duration: 0.5 })
        }
    }
}

window.addEventListener('resize', onWindowResize)
window.addEventListener('wheel', onWheel)

/**
 * Animate
 */

const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    raycaster.setFromCamera(mouse, camera)
    const intersects = raycaster.intersectObjects(planes)

    for (const intersect of intersects) {
        intersect.object.material.uniforms.uActivation.value = 1
    }

    for (const plane of planes) {
        if (!intersects.find(intersect => intersect.object === plane)) {
            plane.material.uniforms.uActivation.value = 0
        }
    }

    for (let [i, plane] of planes.entries()) {
        const rotationZ = map(plane.position.x, -widthTotal, widthTotal, Math.PI, -Math.PI);
        gsap.to(plane.rotation, { z: rotationZ, duration: 0.5 })

        plane.material.uniforms.uTime.value = elapsedTime

        if (titles[i]) {
            gsap.to(titles[i].rotation, { z: rotationZ, duration: 0.5 })
        }
        if (indexsText[i]) {
            gsap.to(indexsText[i].rotation, { z: rotationZ, duration: 0.5 })
        }
    }
    renderer.render(scene, camera)
    window.requestAnimationFrame(tick)
}

tick()